import { type Messages } from "@/root"
import { type LiqaElement, NAME } from "../web-component"
import { props2attrs } from "./utils"

/*
 * LIQA Services v4 Backward compatibility
 */

/**
 * An imperative way to create LIQA web component.
 * @example
 * ```javascript
 * const liqa = new Liqa({
 *   license: "xxx-xxx-xxx",
 *   target: document.querySelector("#liqa-container"),
 *   styles: "@import url('/path/to/custom-liqa-styles.css')"
 * })
 * ```
 * @category Component (imperative)
 * @deprecated Use the {@link LiqaElement <hautai-liqa>} web component instead.
 */
export function Liqa(config: LiqaConfig): LiqaElement {
  const { target, ...props } = config
  const liqa = document.createElement(NAME) as LiqaElement

  for (const [key, value] of Object.entries(props2attrs(props))) {
    let str = String(value)
    if (str === "[object Object]") str = JSON.stringify(value)
    liqa.setAttribute(key, str)
  }

  if (target) {
    const container = typeof target === "string" ? document.querySelector(target) : target
    if (container) container.appendChild(liqa)
    else console.warn(`[LIQA] Target element "${target}" does not exist.`)
  }

  return liqa
}

/**
 * Options to configure the LIQA‘s look and behavior.
 * @category Component (imperative)
 * @deprecated Use the {@link LiqaElement <hautai-liqa>} web component with the {@link LiqaAttributes} instead.
 */
export type LiqaConfig = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /**
   * Query selector to the target element or HTMLElement to render LIQA to.
   * @example
   * document.querySelector("#liqa-container")
   */
  target?: Element | string
  /**
   * A string containing valid CSS rules. For more information, see [Styles](/customization/ui/styles) page.
   * @example
   * "@import url('/path/to/custom-liqa-styles.css')"
   */
  styles?: string
  /**
   * Render LIQA inside an iframe instead of Shadow DOM.
   * @default false
   */
  "use-iframe"?: boolean
  /**
   * An object whose keys are messages keys and values are messages texts. For more information, see [Text Messages](/customization/ui/text-messages) page.
   * @example
   * { "preview": { "submit": "Confirm" } }
   */
  messages?: Messages
  /**
   * A preset used to customize AI quality criteria and user flow. For more information, see [Presets](customization/user-flow/presets) page.
   * @default "face"
   */
  preset?: "face" | "face-180" | "hair"
  /**
   * A list of sources to capture the image from: different device cameras or file upload.
   * Check the relevant [user flow customization](/customization/user-flow/image-sources) section to learn about the logic.
   *
   * @default ["front_camera"]
   * @alias `["camera"]`
   * Starting from `6.7.0` version `"camera"` acts as an alias for `"front_camera"`.
   *
   * _Note: For_ `face-180` _preset only_ `"front_camera"` _source is available. The rest will be ignored._
   */
  sources?:
    | ["camera" | "front_camera" | "back_camera"]
    | ["upload"]
    | ["camera" | "front_camera" | "back_camera", "upload"]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   *
   * _Note: The option is only applicable to_ `"hair"` _preset. For other presets it is always_ `"auto"`_._
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will be softened if the lighting conditions are poor.
   * - `"default"`: the requirements will be set to optimal which will provide more details on the photo.
   *
   * _Note: The option is only applicable to_ `"face"` _preset. For other presets it is always_ `"default"`_._
   *
   * @default `"default"`
   */
  "required-lighting"?: "none" | "adaptive" | "default"
  /**
   * A flag to enable or disable the effects during the image capture process.
   *
   * @default false
   */
  effects?: boolean | "true" | "false"
  /**
   * A flag to enable or disable tutorial option on the source selection screen.
   * Enabled only when sources option contain "front_camera" or "back_camera" and "upload".
   *
   * - `"off"`: no tutorial will be shown.
   * - `"video"`: a video tutorial will be shown.
   * - `"image"`: an image tutorial will be shown. Can be used as a fallback for reducing the loading time.
   *
   * @default "off"
   */
  tutorial?: "off" | "image" | "video"
  /**
   * A flag to set the entry point of the LIQA.
   *
   * - `"capture"`: the entry point will be set to the capture screen.
   * - `"tutorial"`: the entry point will be set to the tutorial screen.
   *
   * @default "capture"
   */
  "entry-point"?: string | "capture" | "tutorial"
  /**
   * A flag to set the quality check behavior.
   *
   * - `off`: the quality check will be disabled.
   * - `strict`: the quality check will be strict and the user will not be able to continue if the quality criteria are not met.
   * - `normal`: the quality check will be normal and the user will be able to continue even if the quality criteria are not met.
   *
   * @default `"off"`
   */
  "quality-check"?: "off" | "normal" | "strict"
  /**
   * An array specifying which capture sources should enforce quality checks.
   *
   * The quality check behavior (set via the `qualityCheck` property) will only be applied for the sources included in this array.
   * Available values are:
   * - `front_camera`: Enforces quality check on images captured from the front camera.
   * - `back_camera`: Enforces quality check on images captured from the back camera.
   * - `upload`: Enforces quality check on images provided via file upload.
   * - `companion`: Enforces quality check on images captured via a companion source.
   *
   * If not provided, quality checks will be applied for all sources.
   *
   * @default ["front_camera","back_camera","upload","companion"]
   */
  "quality-check-sources"?: ("front_camera" | "back_camera" | "upload" | "companion")[]
  /**
   * A flag to enable or disable best frame selection during image capture.
   *
   * When enabled, LIQA will track multiple frames during the capture process
   * and automatically select the frame with the highest quality metrics.
   *
   * @default `"true"`
   */
  "best-frame-selection"?: "true" | "false"
}
